import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { useEffect, useState, useCallback } from "react";
import AssignedJobsWithTBD from "./AssignedJobsWithTBD";
import TopNav from "../TopNav/TopNav";
import Home from "../MainPage/Home";
import { useAuth } from "../../contexts/AuthContext";
import { useSelector, useDispatch } from "react-redux";
import { Accordion, Spinner, Alert } from "react-bootstrap";
import { selectApiUrls, resetApiUrls } from "../../redux/slices/apiUrlSlice";
import { clearApiStatus, updateApiStatus } from "../../redux/slices/apiStatusSlice";
import { clearJobDetails } from "../../redux/slices/jobDetailsSlice";
import { clearHasValues } from "../../redux/slices/hasValuesSlice";
import { clearDropdowns, fetchUserDropdowns } from "../../redux/slices/dropdownSlice";
import { fetchTbdData, setTbdList } from "../../redux/slices/tbdApiSlice";
import { useMsal } from "@azure/msal-react";
import { fetchData } from "../../redux/slices/apiCallSlice";
import { usePermissions } from "../../hooks/usePermissions";

function UserPageContainer() {
  const { isAuthenticated } = useAuth();
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const tbdList = useSelector((state) => state.tbdApi.tbdList);
  const { canView, isLoading: isPermissionsLoading } = usePermissions();

  const canAccessUserHome = canView("home.user-home");
  const canViewAssignedJobs = canView("user.assigned-jobs");

  const [apiData, setApiData] = useState({
    generalProject: [],
  });
  const [apiLoading, setApiLoading] = useState({
    generalProject: false,
  });
  const [apiErrors, setApiErrors] = useState({
    generalProject: null,
  });
  useGuardedRealtimeList({ apiKey: 'tbdValues', enabled: isAuthenticated && canViewAssignedJobs, apply: rows => dispatch(setTbdList(rows)) });
  useGuardedRealtimeList({ apiKey: 'generalProject', enabled: isAuthenticated && canViewAssignedJobs, blocked: apiLoading.generalProject,
    apply: rows => setApiData(previous => ({ ...previous, generalProject: rows })) });

  useEffect(() => {
    dispatch(resetApiUrls());
    dispatch(clearApiStatus());
    dispatch(clearJobDetails());
    dispatch(clearHasValues());
    dispatch(clearDropdowns());

    if (accounts.length > 0) {
      dispatch(fetchUserDropdowns({ instance, accounts }));
    }
  }, [dispatch, instance, accounts]);

  const fetchApiData = useCallback(async (endpoint, tableName) => {
    setApiLoading((prev) => ({ ...prev, [tableName]: true }));
    setApiErrors((prev) => ({ ...prev, [tableName]: null }));

    try {
      dispatch(updateApiStatus({ tableName, status: "pending" }));
      const data = await dispatch(fetchData({ url: endpoint })).unwrap();
      setApiData((prev) => ({ ...prev, [tableName]: data }));
      dispatch(updateApiStatus({ tableName, status: "success" }));
      return data;
    } catch (error) {
      console.error(`Error fetching ${tableName}:`, error);
      const errorMessage =
        error.response?.data?.message || error.message || `Failed to fetch ${tableName}`;
      setApiErrors((prev) => ({ ...prev, [tableName]: errorMessage }));
      dispatch(updateApiStatus({
        tableName,
        status: "failed",
        error: error.response?.status || 500,
      }));
      throw error;
    } finally {
      setApiLoading((prev) => ({ ...prev, [tableName]: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!canViewAssignedJobs) {
      return;
    }

    dispatch(fetchTbdData()).unwrap().catch((error) => {
      console.error("UserPageContainer: Error fetching TBD data:", error);
    });
  }, [dispatch, canViewAssignedJobs]);

  useEffect(() => {
    if (apiUrls.generalProject && isAuthenticated && canViewAssignedJobs) {
      fetchApiData(apiUrls.generalProject, "generalProject");
    }
  }, [apiUrls.generalProject, fetchApiData, isAuthenticated, canViewAssignedJobs]);

  const isCriticalDataLoading = apiLoading.generalProject;

  return (
    <div>
      <TopNav />
      {!isAuthenticated && <Home />}

      {isAuthenticated && (
        <>
          {isPermissionsLoading && (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Loading permissions...</p>
            </div>
          )}

          {!isPermissionsLoading && !canAccessUserHome && (
            <Alert variant="danger" className="m-3">
              <Alert.Heading>Access Denied</Alert.Heading>
              <p>User Home is not available for this account.</p>
            </Alert>
          )}

          {!isPermissionsLoading && canAccessUserHome && (
            <>
              {isCriticalDataLoading && (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading data...</p>
                </div>
              )}

              {apiErrors.generalProject && (
                <Alert variant="danger" className="m-3">
                  <Alert.Heading>Error Loading Data</Alert.Heading>
                  <p>{apiErrors.generalProject}</p>
                </Alert>
              )}

              {!isCriticalDataLoading && (
                <Accordion defaultActiveKey="0" className="mt-3">
                  {canViewAssignedJobs && (
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        Assigned Jobs
                        {apiLoading.generalProject && (
                          <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                      </Accordion.Header>
                      <Accordion.Body>
                        <AssignedJobsWithTBD
                          jobs={apiData.generalProject}
                          tbdList={tbdList}
                          loading={apiLoading.generalProject}
                          error={apiErrors.generalProject}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                </Accordion>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default UserPageContainer;
